import React, { useState, useEffect, useCallback } from "react";
import cn from "classnames";
import { FormattedMessage } from "react-intl";

import { DisplayName } from "../../../store/user/models"
import routing from "../../../routing";
import { profilePictureHost } from "../../../paths";
import Icon from "../../../icon";

import Notifications from "../Notifications";
import { StaffLines } from "../StaffLines";
import ChangeLanguage from "./ChangeLanguage";

import {
    StyledNavRight,
    MenuItem,
    StyledLogin,
    Caret,
    StyledStaffLinesRight,
    StaffLinesRightStart,
    UserImage,
    Usermenu,
    UserName,
    Account,
    Logout,
    MobileMenuOpener,
    NotificationCount,
    SmallIcon,
    StyledNotification,
    User,
    DarkModeButton,
} from "./styles";
import messages from "./messages";
import { IProps } from "./interfaces";
import Icons from "../../../icons";

const NavRight = ({
    loggedIn,
    admin,
    user,
    mobileMenuOpen,
    handleOpenMobileMenu,
    logout,
    isLogin,
    profiled,
    isModeration,
    isAccount,
    isSignup,
    isPathHelp,
    isTranslate,
    isAboutUs,
    isTeachingSettings,
    darkMode,
    setDarkMode,
}: IProps) => {
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const clickOutsideUsermenu = useCallback(() => {
        setUserMenuOpen(false);
        window.removeEventListener("click", clickOutsideUsermenu);
    }, [setUserMenuOpen]);
    const toggleUsermenu = () => {
        if (!userMenuOpen) {
            setTimeout(() => {
                window.addEventListener("click", clickOutsideUsermenu);
            });
        } else {
            window.removeEventListener("click", clickOutsideUsermenu);
        }
        setUserMenuOpen(old => !old);
    };
    useEffect(() => () => {
        window.removeEventListener("click", clickOutsideUsermenu);
    }, [])

    const toggleDarkMode = () => {
        setDarkMode(!darkMode);
    };

    return (
        <StyledNavRight>
            {loggedIn && (
                <MenuItem $isActive={isAboutUs === 1 || undefined} className="HideLabelOnMd" to={"/" + routing.aboutUs}>
                    <Icon i="aboutus" />
                    <span><FormattedMessage {...messages.aboutUs} /></span>
                </MenuItem>
            )}
            {(admin || (user.moderator && user.moderator.accepted)) && (
                <MenuItem
                    $isActive={isModeration === 1 || undefined}
                    className="HideLabelOnMd"
                    to={"/" + (admin ? routing.moderation + "/users" : routing.testModeration)}
                >
                    <Icon i="admin" />
                    <span><FormattedMessage {...messages.moderation} /></span>
                </MenuItem>
            )}
            {user.isTranslator && (
                <MenuItem $isActive={isTranslate === 1 || undefined} className="HideLabelOnMd" to={"/" + routing.translate}>
                    <Icon i="translate" />
                    <span><FormattedMessage {...messages.translate} /></span>
                </MenuItem>
            )}
            {loggedIn && (
                <MenuItem $isActive={isPathHelp === 1 || undefined} to={"/" + routing.help}>
                    <Icon i="help" />
                    <span><FormattedMessage {...messages.help} /></span>
                </MenuItem>
            )}
            {!loggedIn && (
                <>
                    <MenuItem $isActive={isSignup === 1 || undefined} to={"/" + routing.signup}>
                        <Icon i="signup" />
                        <span><FormattedMessage {...messages.registration} /></span>
                    </MenuItem>
                    <MenuItem $isActive={isLogin === 1 || undefined} to={"/" + routing.login}>
                        <Icon i="login" />
                        <span><FormattedMessage {...messages.login} /></span>
                    </MenuItem>
                    <MenuItem $isActive={isAboutUs === 1 || undefined} to={"/" + routing.aboutUs}>
                        <Icon i="aboutus" />
                        <span><FormattedMessage {...messages.aboutUs} /></span>
                    </MenuItem>
                    <MenuItem $isActive={isPathHelp === 1 || undefined} to={"/" + routing.help}>
                        <Icon i="help" />
                        <span><FormattedMessage {...messages.help} /></span>
                    </MenuItem>
                    <DarkModeButton isActive={darkMode} onClick={toggleDarkMode} >
                        <Icons.darkMode />
                    </DarkModeButton>
                    <ChangeLanguage />
                </>
            )}

            {loggedIn && (
                <>
                    {!user.isStudent && (
                        <Notifications>
                            {({ newNotifications, toggleNotification, notificationsOpen }: any) => (
                                <StyledNotification
                                    className={cn(notificationsOpen && "Active")}
                                    onClick={toggleNotification}
                                >
                                    <Icon i="bell" />
                                    {newNotifications > 0 && <NotificationCount>{newNotifications}</NotificationCount>}
                                </StyledNotification>
                            )}
                        </Notifications>
                    )}
                    <DarkModeButton isActive={darkMode} onClick={toggleDarkMode} >
                        <Icons.darkMode />
                    </DarkModeButton>

                    <User onClick={toggleUsermenu}>
                        <UserImage>
                            {user.profilePicture ? (
                                user.profilePicture.length === 16 ? (
                                    <img src={profilePictureHost + user.profilePicture + ".jpg"} alt="profile" />
                                ) : (
                                    <img src={user.profilePicture} alt="profile" />
                                )
                            ) : (
                                <Icon i="user" />
                            )}
                        </UserImage>
                        <UserName>
                            {user.isStudent
                                ? (user.language === "hu" ? `${user.last_name} ${user.first_name}` : `${user.first_name} ${user.last_name}`)
                                : !user.displayName || user.displayName === DisplayName.Username
                                    ? user.username || <FormattedMessage {...messages.username} />
                                    : user.first_name}
                        </UserName>
                        <Caret>
                            <svg width="11" height="6">
                                <polygon points="0,0 11,0 5.5,5.5" />
                            </svg>
                        </Caret>
                        {userMenuOpen && (
                            <Usermenu>
                                {(profiled || user.isStudent) && (
                                    <Account $isActive={isAccount === 1 || undefined} to={"/" + routing.account}>
                                        <SmallIcon>
                                            <Icon i="account" />
                                        </SmallIcon>
                                        <FormattedMessage {...messages.account} />{user.key ? ` (${user.key})` : ''}
                                    </Account>
                                )}
                                {profiled && (
                                    <Account $isActive={isTeachingSettings === 1 || undefined} to={"/" + routing.teachingSettings}>
                                        <SmallIcon>
                                            <Icon i="settings" />
                                        </SmallIcon>
                                        <FormattedMessage {...messages.teachingSettings} />
                                    </Account>
                                )}
                                <Logout onClick={logout}>
                                    <SmallIcon>
                                        <Icon i="logout" />
                                    </SmallIcon>
                                    <FormattedMessage {...messages.logout} />
                                </Logout>
                            </Usermenu>
                        )}
                    </User>
                </>
            )}

            <MobileMenuOpener onClick={handleOpenMobileMenu}>
                <div className={cn(mobileMenuOpen && "Active")}>
                    <div className={cn("Line", "Line1")}></div>
                    <div className={cn("Line", "Line2")}></div>
                    <div className={cn("Line", "Line3")}></div>
                </div>
            </MobileMenuOpener>
            <StyledStaffLinesRight>
                <StaffLines />
                <StaffLinesRightStart width="24" height="24" viewBox="0 0 24 24">
                    <rect x="0" y="0" width="3" height="25"></rect>
                    <rect x="5" y="0" width="1" height="25"></rect>
                    <circle cy="9.5" cx="9" r="1" width="2" height="2" />
                    <circle cy="15.5" cx="9" r="1" width="2" height="2" />
                </StaffLinesRightStart>
            </StyledStaffLinesRight>
        </StyledNavRight>
    );
}

export default NavRight;
